/**
 * CustomerFilters Component
 * Collapsible filter panel for customer list
 */

import { useState } from 'react'
import { X, Bookmark, Plus, Trash2 } from 'lucide-react'
import { useCustomerStore, useActiveFilterCount } from '@/store/customerStore'
import { mockCAs } from '@/data/mockCustomers'
import { INDIAN_STATES } from '@/types/customer'
import type { CustomerStatus, Occupation, IncomeRange } from '@/types/customer'

export default function CustomerFilters() {
  const {
    filters,
    setFilters,
    clearFilters,
    isFilterPanelOpen,
    savedViews,
    saveCurrentView,
    applySavedView,
    deleteSavedView,
  } = useCustomerStore()
  const activeFilterCount = useActiveFilterCount()

  const [selectedViewId, setSelectedViewId] = useState<string>('')
  const [isSavingView, setIsSavingView] = useState(false)
  const [viewName, setViewName] = useState('')

  if (!isFilterPanelOpen) return null

  const handleSaveView = () => {
    if (!viewName.trim()) return
    saveCurrentView(viewName.trim())
    setViewName('')
    setIsSavingView(false)
  }

  const handleApplyView = (viewId: string) => {
    setSelectedViewId(viewId)
    if (viewId) {
      applySavedView(viewId)
    }
  }

  const handleDeleteView = (viewId: string) => {
    deleteSavedView(viewId)
    if (selectedViewId === viewId) {
      setSelectedViewId('')
    }
  }

  const statusOptions: { value: CustomerStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'archived', label: 'Archived' },
  ]

  const occupationOptions: { value: Occupation | 'all'; label: string }[] = [
    { value: 'all', label: 'All Occupations' },
    { value: 'salaried', label: 'Salaried' },
    { value: 'self_employed', label: 'Self Employed' },
    { value: 'business', label: 'Business' },
    { value: 'retired', label: 'Retired' },
    { value: 'other', label: 'Other' },
  ]

  const incomeRangeOptions: { value: IncomeRange | 'all'; label: string }[] = [
    { value: 'all', label: 'All Income Ranges' },
    { value: '0-5L', label: '0 - 5 Lakhs' },
    { value: '5-10L', label: '5 - 10 Lakhs' },
    { value: '10-25L', label: '10 - 25 Lakhs' },
    { value: '25-50L', label: '25 - 50 Lakhs' },
    { value: '50L+', label: '50 Lakhs+' },
  ]

  return (
    <div className="card card-elevated p-4 animate-in slide-in-from-top-2 duration-200 space-y-4">
      {/* Saved Views Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-surface-overlay">
        <div className="flex items-center gap-2">
          <Bookmark className="w-4 h-4 text-primary-600 dark:text-primary-400" />
          <span className="text-sm font-medium text-text-primary">Saved Filter Views:</span>
          <select
            value={selectedViewId}
            onChange={(e) => handleApplyView(e.target.value)}
            className="input input-bordered input-sm text-sm"
          >
            <option value="">Custom / Default View</option>
            {savedViews.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>

          {selectedViewId && (
            <button
              onClick={() => handleDeleteView(selectedViewId)}
              className="btn btn-ghost btn-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Delete this saved view"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div>
          {!isSavingView ? (
            <button
              type="button"
              onClick={() => setIsSavingView(true)}
              className="btn btn-outline btn-xs flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" />
              Save Current View
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="View name (e.g. Salaried Delhi)"
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                className="input input-bordered input-xs"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSaveView()}
              />
              <button
                type="button"
                onClick={handleSaveView}
                disabled={!viewName.trim()}
                className="btn btn-primary btn-xs"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsSavingView(false)
                  setViewName('')
                }}
                className="btn btn-ghost btn-xs"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Status
          </label>
          <select
            value={filters.status || 'all'}
            onChange={(e) => {
              setSelectedViewId('')
              setFilters({ status: e.target.value as CustomerStatus | 'all' })
            }}
            className="input input-bordered w-full"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Assigned CA Filter */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Assigned CA
          </label>
          <select
            value={filters.assignedCA || 'all'}
            onChange={(e) => {
              setSelectedViewId('')
              setFilters({ assignedCA: e.target.value })
            }}
            className="input input-bordered w-full"
          >
            <option value="all">All CAs</option>
            {mockCAs.map((ca) => (
              <option key={ca.id} value={ca.id}>
                {ca.firstName} {ca.lastName}
              </option>
            ))}
          </select>
        </div>

        {/* Occupation Filter */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Occupation
          </label>
          <select
            value={filters.occupation || 'all'}
            onChange={(e) => {
              setSelectedViewId('')
              setFilters({ occupation: e.target.value as Occupation | 'all' })
            }}
            className="input input-bordered w-full"
          >
            {occupationOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Income Range Filter */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Income Range
          </label>
          <select
            value={filters.incomeRange || 'all'}
            onChange={(e) => {
              setSelectedViewId('')
              setFilters({ incomeRange: e.target.value as IncomeRange | 'all' })
            }}
            className="input input-bordered w-full"
          >
            {incomeRangeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* State Filter */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            State
          </label>
          <select
            value={filters.state || 'all'}
            onChange={(e) => {
              setSelectedViewId('')
              setFilters({ state: e.target.value })
            }}
            className="input input-bordered w-full"
          >
            <option value="all">All States</option>
            {INDIAN_STATES.map((state) => (
              <option key={state.code} value={state.name}>
                {state.name}
              </option>
            ))}
          </select>
        </div>

        {/* Checkboxes */}
        <div className="sm:col-span-2 lg:col-span-1 flex flex-col justify-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.hasPendingAssessments || false}
              onChange={(e) => {
                setSelectedViewId('')
                setFilters({ hasPendingAssessments: e.target.checked })
              }}
              className="w-4 h-4 rounded border-surface-overlay text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-text-primary">Has Pending Assessments</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.hasPendingDocuments || false}
              onChange={(e) => {
                setSelectedViewId('')
                setFilters({ hasPendingDocuments: e.target.checked })
              }}
              className="w-4 h-4 rounded border-surface-overlay text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-text-primary">Has Pending Documents</span>
          </label>
        </div>

        {/* Clear/Apply Actions */}
        <div className="sm:col-span-2 lg:col-span-1 xl:col-span-2 flex items-end justify-end gap-2">
          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                setSelectedViewId('')
                clearFilters()
              }}
              className="btn btn-ghost btn-sm flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
